'use client';

import { useState, MouseEvent, useEffect, Suspense } from 'react';
import { Button } from '@/features/uiKit/Button';
import { IconButton } from '@/features/uiKit/IconButton';
import { Drawer } from '@/features/uiKit/Drawer';
import { Link } from '@/features/uiKit/Link';
import { List, ListItem, ListItemButton, ListItemIcon, ListItemText } from '@/features/uiKit/List';
import { Stack } from '@/features/uiKit/Stack';
import { X } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { MenuIcon } from '@/features/uiKit/icons';
import { HeaderUIProps } from './types';
import { getUrlStart } from '../Lang/getUrlStart';
import { landingContentFrame } from '@/features/Landing/landingSettings';

function HeaderUIComponent({ lang, links, buttons, transparentOnTop, logoHref }: HeaderUIProps) {
  const [isOpenMainMenu, setIsOpenMainMenu] = useState(false);
  const [isHighlightJoin, setIsHighlightJoin] = useState(false);
  const [isBlurHeader, setIsBlurHeader] = useState(false);
  const [isFixedHeader, setIsFixedHeader] = useState(true);

  useEffect(() => {
    const isWindow = typeof window !== 'undefined';
    if (!isWindow) {
      return;
    }

    const urlsForNormalHeader = [
      getUrlStart(lang) + 'blog',
      getUrlStart(lang) + 'scenarios',
      getUrlStart(lang) + 'contacts',
    ];

    const urlPath = location.pathname;
    const isStaticHeader = !urlsForNormalHeader.find((url) => urlPath.startsWith(url));
    setIsFixedHeader(isStaticHeader);

    const onScrollHandler = () => {
      if (window.scrollY > 0) {
        setIsBlurHeader(true);
      } else {
        setIsBlurHeader(false);
      }

      if (window.scrollY > 800) {
        setIsHighlightJoin(true);
      } else {
        setIsHighlightJoin(false);
      }
    };

    window.addEventListener('scroll', onScrollHandler);
    return () => {
      window.removeEventListener('scroll', onScrollHandler);
    };
  }, []);

  const router = useRouter();
  const isTransparentBar = Boolean(transparentOnTop && !isBlurHeader);

  const onlyNavigate = (url: string) => {
    router.push(url);
  };

  const navigateTo = (url: string, e: MouseEvent<unknown, unknown>) => {
    const isNewTab = e.ctrlKey || e.metaKey;
    if (isNewTab) {
      return;
    } else {
      e.preventDefault();
      router.push(url);
    }
  };
  return (
    <>
      <Stack
        component={'header'}
        sx={{
          width: '100%',
          alignItems: 'center',
          justifyContent: 'center',

          position: isFixedHeader ? 'fixed' : 'absolute',
          top: 0,
          left: 0,
          zIndex: 999,
          backgroundColor: isTransparentBar ? 'transparent' : '#fff',
          backdropFilter: isTransparentBar ? 'blur(0px)' : 'blur(10px)',
          borderBottom: isTransparentBar ? 'none' : '1px solid rgba(0, 0, 0, 0.08)',

          transition: 'all 0.3s ease-in-out',

          '.logo-link.menu-link': {
            paddingLeft: '8px',
            paddingRight: '10px',
          },
          '.menu-link': {
            height: '60px',
            fontWeight: 360,
            color: '#111',
            display: 'flex',
            alignItems: 'center',
            minHeight: '100%',
            textDecoration: 'none',
            padding: '0 20px',
            borderRadius: '5px',
            ':hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.06)',
            },
          },
          '@media (max-width: 650px)': {
            position: isFixedHeader ? 'fixed' : 'absolute',
            '.hideOnMobile': {
              display: 'none !important',
            },
          },
        }}
      >
        <Stack
          sx={{
            ...landingContentFrame,
            gap: '10px',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingTop: '5px',
            paddingBottom: '5px',
          }}
        >
          <Stack
            sx={{
              flexDirection: 'row',
              gap: '0px',
              height: '100%',
            }}
          >
            <Stack
              component={'a'}
              href={logoHref}
              onClick={(e) => navigateTo(logoHref, e)}
              className="menu-link logo-link"
              sx={{
                marginRight: '85px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginLeft: '-13px',
                img: { objectFit: 'cover', objectPosition: 'left center' },
                '.big_logo': {
                  display: 'block',
                },
                '.small_logo': {
                  display: 'none',
                },
                '@media (max-width: 1000px)': {
                  marginRight: '0px',
                },
                '@media (max-width: 850px)': {
                  '.big_logo': {
                    display: 'none',
                  },
                  '.small_logo': {
                    display: 'block',
                  },
                },
              }}
            >
              {/* logo-dark.svg has empty space right of the text; these widths crop it off. */}
              <img
                src="/logo-dark.svg"
                alt="logo"
                width={141}
                height={36}
                className="big_logo"
              />
              <img
                src="/logo-dark.svg"
                alt="logo"
                width={110}
                height={28}
                className="small_logo"
              />
            </Stack>

            {links.map((link) => (
              <Link
                key={link.href}
                href={`${link.href}`}
                onClick={(e) => navigateTo(`${link.href}`, e)}
                className="menu-link hideOnMobile"
              >
                {link.title}
              </Link>
            ))}
          </Stack>

          <Stack
            sx={{
              flexDirection: 'row',
              gap: '10px',
              alignItems: 'center',
              height: '100%',
            }}
          >
            {buttons.map((button) => (
              <Stack key={button.href} className={button.isSolid ? '' : 'hideOnMobile'}>
                <Button
                  href={`${button.href}`}
                  onClick={(e) => navigateTo(`${button.href}`, e)}
                  tone="primary"
                  size="small"
                  data-analytics={button.analyticsId || 'header-cta'}
                >
                  {button.title}
                </Button>
              </Stack>
            ))}

            <Stack
              sx={{
                display: 'none',
                color: '#111',
                '@media (max-width: 650px)': {
                  display: 'flex',
                  marginTop: '3px',
                  marginRight: '10px',
                },
              }}
            >
              <IconButton
                onClick={() => setIsOpenMainMenu(true)}
                title="Open main menu"
                aria-label="Open main menu"
              >
                <MenuIcon />
              </IconButton>
            </Stack>
          </Stack>
        </Stack>
      </Stack>

      <Drawer
        open={isOpenMainMenu}
        onClose={() => setIsOpenMainMenu(false)}
        anchor="right"
        slotProps={{
          paper: {
            sx: {
              backgroundColor: '#fff',
              backgroundImage: 'none',
              color: '#111',
            },
          },
        }}
      >
        <Stack
          sx={{
            width: '240px',
            height: '100svh',
            boxSizing: 'border-box',
            flexDirection: 'column',
            justifyContent: 'space-between',
            padding: '0px',
          }}
          onClick={() => setIsOpenMainMenu(false)}
        >
          <Stack
            sx={{
              alignItems: 'flex-end',
              color: '#111',
            }}
          >
            <IconButton
              onClick={() => setIsOpenMainMenu(false)}
              title="Close main menu"
              aria-label="Close main menu"
            >
              <X />
            </IconButton>
          </Stack>

          <List
            sx={{
              padding: '0 0 0 10px',
            }}
          >
            {links.map((link) => (
              <ListItem disablePadding key={link.href}>
                <ListItemButton
                  onClick={() => {
                    onlyNavigate(`${link.href}`);
                    setIsOpenMainMenu(false);
                  }}
                  sx={{
                    color: '#111',
                    ':hover': { backgroundColor: 'rgba(0, 0, 0, 0.06)' },
                  }}
                >
                  <ListItemIcon sx={{ paddingRight: '0', minWidth: '40px', color: '#111' }}>
                    <link.icon />
                  </ListItemIcon>
                  <ListItemText primary={link.title} sx={{ color: '#111' }} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>

          <Stack
            sx={{
              padding: '10px',
            }}
          >
            {buttons.map((button) => (
              <Button
                key={button.href}
                href={`${button.href}`}
                onClick={(e) => {
                  setIsOpenMainMenu(false);
                  navigateTo(`${button.href}`, e);
                }}
                tone="primary"
                data-analytics={button.analyticsId || 'header-cta'}
              >
                {button.title}
              </Button>
            ))}
          </Stack>
        </Stack>
      </Drawer>
    </>
  );
}

export function HeaderUI(props: HeaderUIProps) {
  return (
    <Suspense>
      <HeaderUIComponent {...props} />
    </Suspense>
  );
}
